import React from "react";
import { GraduationCap, Award, Stethoscope, HeartHandshake, CheckCircle2, Microscope, Star } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import AppointmentCTA from "../components/AppointmentCTA";
import MedicalCitations from "../components/MedicalCitations";
import { clinic, images, expertise, procedures, reviews, stats } from "../data/mock";

const About = () => {
  return (
    <div>
      <SEO path="/gastro-liver-specialist-dr-indresh-dixit" reviews={reviews} />
      <PageHero breadcrumb="About"
        title="Meet Dr. Indresh Dixit"
        subtitle={`${clinic.qualifications} — ${clinic.specialization} with ${clinic.experience} years of dedicated experience in Varanasi.`} />

      {/* PROFILE */}
      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative flex justify-center">
            <div className="absolute inset-0 bg-gradient-to-tr from-sky-400 to-sky-200 rounded-[2.5rem] rotate-3 scale-95 opacity-40 blur-xl" />
            <div className="relative bg-gradient-to-b from-sky-100 to-white rounded-[2.5rem] p-4 shadow-2xl shadow-sky-200/50">
              <img src={images.doctor} alt={clinic.doctorName} className="w-full max-w-sm rounded-[2rem]" />
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="About the Doctor" title="A Trusted Name in Liver Care" />
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>{clinic.doctorName} is one of the most respected liver specialists and gastroenterologists in Varanasi, known for combining precise diagnosis with genuinely personalized treatment. Over {clinic.experience} years of practice, he has helped thousands of patients recover from fatty liver, cirrhosis, hepatitis, jaundice and complex digestive disorders.</p>
              <p>His approach is rooted in honesty and clarity — patients receive a clear explanation of their condition, the reasoning behind every test and a realistic, achievable treatment plan. He believes early detection and lifestyle correction can reverse most liver conditions before they become serious.</p>
              <p>Practicing at {clinic.clinicName}, he uses advanced tools such as FibroScan and high-definition endoscopy to ensure accurate results and better outcomes for every patient.</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {stats.slice(0, 2).map((s) => (
                <div key={s.label} className="bg-sky-50 rounded-xl p-5 border border-sky-100">
                  <div className="font-heading text-3xl font-bold text-sky-600">{s.value}</div>
                  <div className="text-sm text-slate-600 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QUALIFICATIONS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Credentials" title="Qualifications & Professional Journey" />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: GraduationCap, title: "Education", items: ["MBBS — Bachelor of Medicine", "MD — General Medicine", "DM — Gastroenterology"] },
              { icon: Stethoscope, title: "Specialization", items: ["Liver Diseases (Hepatology)", "Gastroenterology", "Advanced Endoscopy"] },
              { icon: Award, title: "Experience", items: [`${clinic.experience} years of clinical practice`, "20,000+ patients treated", "Two clinic locations in Varanasi"] },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-sky-500 text-white grid place-items-center mb-5"><c.icon size={24} /></div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">{c.title}</h3>
                <ul className="space-y-2">
                  {c.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2 text-slate-600 text-sm"><CheckCircle2 size={17} className="text-sky-500 mt-0.5 shrink-0" /> {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERTISE + PROCEDURES */}
      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading center={false} eyebrow="Areas of Expertise" title="Conditions Treated" />
            <div className="flex flex-wrap gap-3">
              {expertise.map((e, i) => (
                <span key={i} className="bg-sky-50 text-sky-700 text-sm font-medium px-4 py-2 rounded-full border border-sky-100">{e}</span>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading center={false} eyebrow="Procedures" title="Advanced Procedures Performed" />
            <div className="grid sm:grid-cols-2 gap-3">
              {procedures.map((p, i) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3 shadow-sm">
                  <Microscope size={18} className="text-sky-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-100 shadow-sm max-w-4xl mx-auto text-center">
            <div className="w-14 h-14 rounded-2xl bg-sky-500 text-white grid place-items-center mx-auto mb-6"><HeartHandshake size={28} /></div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Clinical Philosophy</h2>
            <p className="text-slate-600 leading-relaxed text-lg">
              “Every liver tells a story. My role is to listen carefully, diagnose accurately and treat honestly — empowering each patient with the knowledge and care they need to protect their liver for life.”
            </p>
            <p className="mt-5 font-heading font-semibold text-sky-600">— {clinic.doctorName}</p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Patient Testimonials" title="Stories of Recovery" />
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {reviews.slice(0, 2).map((r, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
                <div className="flex gap-1 mb-4">{[...Array(r.rating)].map((_, j) => <Star key={j} size={16} className="fill-amber-400 text-amber-400" />)}</div>
                <p className="text-slate-600 leading-relaxed mb-5">"{r.text}"</p>
                <div className="font-semibold text-slate-900">{r.name}</div>
                <div className="text-sm text-slate-500">{r.location}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MedicalCitations />
      <AppointmentCTA title="Consult Varanasi's Trusted Liver Specialist" />
    </div>
  );
};

export default About;
